import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreDocument } from '@angular/fire/firestore';
import { AuthService } from './auth.service';
import { firestore } from 'firebase/app';
import { map, switchMap } from 'rxjs/operators';
import { Observable, combineLatest, of } from 'rxjs';
import { SharedService } from "./shared.service";
import { environment } from '../../environments/environment';
import * as moment from 'moment-timezone';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  // static AVATAR_SELECT = "Laerでのご支援ありがとうございます🌸<br/>こちらから通話に使用するアバターを選択してください。 <br/><a href='//avatar/select'>アバターの選択</a>"
  static AVATAR_SELECT = "Laerでのご支援ありがとうございます🌸<br/><br/>以下から通話に使用するアバターの選択と、アバター通話の動作確認をすると通話の予約ができるようになります。 <br/><br/><a href='" + environment.site_url + "/avatar/select'>アバターの選択</a> <br/><a href='" + environment.site_url + "/avatar/call-test'>アバター通話の動作確認</a> <br/><br/>※通話の予約画面は、通話を予約したいVTuberのメッセージ画面で、右上の[カレンダーアイコン]をクリックすると表示されます。"

  constructor(
    private afs: AngularFirestore,
    private auth: AuthService
  ) {}

  get(chatId) {
    return this.afs
      .collection<any>('chats')
      .doc(chatId)
      .snapshotChanges()
      .pipe(
        map(doc => {
          return { id: doc.payload.id, ...doc.payload.data() };
        })
      );
  }

  getUserChats() {
    const compareField = this.auth.user.role === AuthService.USER_ROLE ? 'user.uid' : 'vtuber.uid';
    return this.afs
      .collection('chats', ref => ref.where(compareField, '==', this.auth.user.uid).orderBy('lastMessageAt', 'desc'))
      .snapshotChanges()
      .pipe(
        map(actions => {
          return actions.map(a => {
            const data: Object = a.payload.doc.data();
            const id = a.payload.doc.id;

            return { id, ...data };
          });
        })
      );
  }

  getSystemNotifications() {
    const nots = this.afs
    .collection('notifications', ref => ref.where('target', '==', this.auth.user.uid).where('createdAt', '>', this.auth.user.createdAt).orderBy('createdAt', 'desc'))
    .snapshotChanges()
    .pipe(
      map(actions => {
        return actions.map(a => {
          const data: Object = a.payload.doc.data();
          const id = a.payload.doc.id;

          return { id, ...data };
        });
      })
    );

    return combineLatest(nots)
  }

  getLastNotification() {
    const nots = this.afs
    .collection('notifications', ref => ref.where('target', '==', this.auth.user.uid).where('createdAt', '>', this.auth.user.createdAt).orderBy('createdAt', 'desc').limit(1))
    .snapshotChanges()
    .pipe(
      map(actions => {
        return actions.map(a => {
          const data: Object = a.payload.doc.data();
          const id = a.payload.doc.id;

          return { id, ...data };
        });
      })
    );

    return combineLatest(nots)
  }

  getTargetUser(room) {
    const targetUserUID = this.auth.user.role === AuthService.USER_ROLE ? room.vtuber.uid : room.user.uid;

    return this.afs.doc<any>(`users/${targetUserUID}`).get().toPromise();
  }

  public updateChatData(data, merge = true) {
    const chatRef: AngularFirestoreDocument<any> = this.afs.doc(`chats/${data.uid}`);

    return chatRef.set(data, { merge });
  }

  async change(vtuber: any, payjpPayID: any, index: number) {
    let chat = (await this.afs.doc<any>(`chats/${this.auth.user.chats[index]}`).get().toPromise()).data()
    chat.lastMessageAt = Date.now()
    chat.messages.push({
      message: vtuber.welcome.message,
      createdAt: Date.now(),
      sender: vtuber.uid,
      type: 'message'
    })
    chat.uid = this.auth.user.chats[index]
    chat.lastMessage = vtuber.welcome.message;
    chat.vtuber.count++
    chat.user.count++
    await this.updateChatData(chat);

    // Adding Histories
    this.historyForPay(vtuber, vtuber.currentPlan, payjpPayID)

    // Change Plans
    let plans = this.auth.user.plans.map((item, ind) => {
      if (index === ind) {
        if (payjpPayID === -1) { // From High plan to lower plan
          item.change = {
            plan_uid: vtuber.currentPlan.uid,
            changedAt: moment(moment().tz('Asia/Tokyo').add(1, "M").format("Y-MM-01 00:00:00")).toDate().getTime()
          }

          return item;
        }

        return {
          vtuber_id: vtuber.uid,
          plan_uid: vtuber.currentPlan.uid,
          startedAt: Date.now(),
          used_period: item.used_period
        }
      }

      return item;
    })

    let params = this.auth.user
    params.plans = plans

    // if avatar is not selected yet
    if (this.auth.user.avatar === undefined && this.auth.user.isAvatarLinkSent === undefined) {
      params.isAvatarLinkSent = true

      this.afs.collection('notifications').add({
        message: ChatService.AVATAR_SELECT,
        type: 'message',
        target: this.auth.user.uid,
        createdAt: Date.now()
      })
    }

    await this.auth.updateUserData(params, false);
    this.auth.fetchUser();

    let supporters = vtuber.supporters.map(item => {
      if (item.user_id === this.auth.user.uid) {
        if (payjpPayID === -1) { // From High plan to lower plan
          item.change = {
            plan_uid: vtuber.currentPlan.uid,
            changedAt: moment(moment().tz('Asia/Tokyo').add(1, "M").format("Y-MM-01 00:00:00")).toDate().getTime()
          }

          return item;
        }

        return {
          user_id: this.auth.user.uid,
          plan_uid: vtuber.currentPlan.uid,
          startedAt: Date.now(),
          supported_period: item.supported_period
        }
      }

      return item;
    })

    let vparams = vtuber;
    delete vparams.currentPlan;
    vparams.supporters = supporters;

    await this.auth.updateUserData(vparams, false)
  }

  async create(vtuber: any, payjpPayID: any) {
    let data: any = {
      user: {
        count: 1,
        uid: this.auth.user.uid
      },
      vtuber: {
        count: 1,
        uid: vtuber.uid
      },
      createdAt: Date.now(),
      lastMessageAt: Date.now(),
      lastMessage: vtuber.welcome.message,
      messages: [
        {
          message: vtuber.welcome.message,
          createdAt: Date.now(),
          sender: vtuber.uid,
          type: 'message'
        },
        // {
        //   message: SharedService.LAER_WELCOME_MESSAGE,
        //   createdAt: Date.now(),
        //   sender: 'laer',
        //   type: 'message'
        // }
      ]
    };

    const docRef = await this.afs.collection('chats').add(data);
    data.uid = docRef.id;
    this.updateChatData(data)

    // Adding Chats
    let chats = this.auth.user.chats ? this.auth.user.chats : []
    chats.push(docRef.id)

    let plans = this.auth.user.plans ? this.auth.user.plans : []
    plans.push({
      vtuber_id: vtuber.uid,
      plan_uid: vtuber.currentPlan.uid,
      startedAt: Date.now(),
      used_period: 0
    })

    let params: any = {
      uid: this.auth.user.uid,
      chats: chats,
      plans: plans
    }

    // if avatar is not selected yet
    if (this.auth.user.avatar === undefined && this.auth.user.isAvatarLinkSent === undefined) {
      params.isAvatarLinkSent = true

      this.afs.collection('notifications').add({
        message: ChatService.AVATAR_SELECT,
        type: 'message',
        target: this.auth.user.uid,
        createdAt: Date.now()
      })
    }

    await this.auth.updateUserData(params);
    this.auth.fetchUser();

    let vchats = vtuber.chats ? vtuber.chats : []
    vchats.push(docRef.id)

    let supporters = vtuber.supporters ? vtuber.supporters : []
    supporters = supporters.filter(item => item.user_id !== this.auth.user.uid)
    supporters.push({
      user_id: this.auth.user.uid,
      plan_uid: vtuber.currentPlan.uid,
      startedAt: Date.now(),
      supported_period: 0
    })

    let vparams = {
      uid: vtuber.uid,
      chats: vchats,
      supporters: supporters
    }

    await this.auth.updateUserData(vparams)

    // Adding Histories
    this.historyForPay(vtuber, vtuber.currentPlan, payjpPayID)

    return docRef.id;
  }

  historyForPay(vtuber, plan, payjpPayID) {
    const data = {
      user_id: this.auth.user.uid,
      vtuber_id: vtuber.uid,
      plan: plan,
      createdAt: Date.now(),
      payjp_id: payjpPayID
    }

    this.afs.collection('histories').add(data);
  }

  async sendMessage(chatId, content, isVtuber, userID = "", vtuberID = "") {
    if (chatId === undefined) return;
    const { uid } = this.auth.user;
    let { vtuber, user } = (await this.afs.doc<any>(`chats/${chatId}`).get().toPromise()).data()

    if (userID !== "" && vtuberID !== "") {
      if (userID !== user.uid || vtuberID !== vtuber.uid) return;
    }

    const data = Object.assign({
      uid,
      createdAt: Date.now()
    }, ...content)

    if (uid) {
      const ref = this.afs.collection('chats').doc(chatId);
      if (isVtuber) {
        vtuber.count++;
      } else {
        user.count++;
      }
      return ref.update({
        messages: firestore.FieldValue.arrayUnion(data),
        vtuber: vtuber,
        user: user,
        lastMessage: content.message,
        lastMessageAt: Date.now()
      });
    }
  }

  async deleteMessage(chat, msg) {
    const { uid } = await this.auth.getUser();

    const ref = this.afs.collection('chats').doc(chat.id);
    if (chat.uid === uid || msg.uid === uid) {
      // Allowed to delete
      delete msg.user;
      return ref.update({
        messages: firestore.FieldValue.arrayRemove(msg)
      });
    }
  }

  joinUsers(chat$: Observable<any>) {
    let chat;
    const joinKeys = {};

    return chat$.pipe(
      switchMap(c => {
        // Unique User IDs
        chat = c;
        let uids = Array.from(new Set(c.messages.map(v => v.sender))).filter(v => v !== 'laer');
        if (!uids.includes(chat.user.uid)) uids.push(chat.user.uid)
        if (!uids.includes(chat.vtuber.uid)) uids.push(chat.vtuber.uid)

        // Firestore User Doc Reads
        const userDocs = uids.map(u =>
          this.afs.doc(`users/${u}`).valueChanges()
        );

        if (this.auth.user.role === AuthService.VTUBER_ROLE) {
          if (chat.user.count > 0) {
            chat.user.count = 0;
            this.updateChatData(chat);
          }
        } else {
          if (chat.vtuber.count > 0) {
            chat.vtuber.count = 0;
            this.updateChatData(chat);
          }
        }

        return userDocs.length ? combineLatest(userDocs) : of([]);
      }),
      map(arr => {
        arr.forEach(v => (joinKeys[(<any>v).uid] = v));
        chat.messages = chat.messages.map(v => {
          if (v.sender === 'laer') {
            return { ...v, user: {
              photoURL: '/assets/laer.svg'
            }}
          }
          return { ...v, user: {
            photoURL: joinKeys[v.sender].photoURL
          }};
        });

        chat.user.name = joinKeys[chat.user.uid].displayName;
        chat.vtuber.name = joinKeys[chat.vtuber.uid].displayName;

        return chat;
      })
    );
  }

  getRooms(chat$: Observable<any>) {
    let rooms = [];
    const joinKeys = {};

    return chat$.pipe(
      switchMap(c => {
        // Unique User IDs
        rooms = c;
        let uids = [];
        rooms.map(chat => {
          if (!uids.includes(chat.user.uid)) {
            uids.push(chat.user.uid)
          }
          if (!uids.includes(chat.vtuber.uid)) {
            uids.push(chat.vtuber.uid)
          }
        })

        // Firestore User Doc Reads
        const userDocs = uids.map(u =>
          this.afs.doc(`users/${u}`).valueChanges()
        );

        return userDocs.length ? combineLatest(userDocs) : of([]);
      }),
      map(arr => {
        arr.forEach(v => (joinKeys[(<any>v).uid] = v));
        return rooms.map(room => {
          let uid = this.auth.user.role === AuthService.USER_ROLE ? room.vtuber.uid : room.user.uid;

          room.target = {
            photoURL: joinKeys[uid].photoURL,
            displayName: joinKeys[uid].displayName,
            badge: this.auth.user.role === AuthService.USER_ROLE ? room.vtuber.count : room.user.count
          }

          return room;
        })
      })
    );
  }

  getNotifications(notification$: Observable<any>) {
    return notification$.pipe(
      map(arr => {
        arr = arr[0]
        arr = arr.sort((a, b) => {
          if (a.createdAt > b.createdAt) return 1;
          if (a.createdAt < b.createdAt) return -1;
          return 0
        })
        return arr
      })
    );
  }

  getLastNotificationMessage(notification$: Observable<any>) {
    return notification$.pipe(
      map(arr => {
        arr = arr[0]
        if (arr.length > 1) {
          arr = arr.sort((a, b) => {
            if (a.createdAt > b.createdAt) return -1;
            if (a.createdAt < b.createdAt) return 1;
            return 0;
          })
        }
        if (arr.length > 0) return arr[0]

        return undefined
      })
    );
  }
}
